// telemetry.ts — generic client telemetry + error reporting for the WI web client.
//
// ONE channel: track(kind, data?, level?) posts a small JSON event to /api/telemetry (the leaderboard
// worker's cookieless collector). It also auto-captures uncaught errors (window 'error') and rejected
// promises ('unhandledrejection'), so a whole class of "works on my machine, breaks in the wild"
// failures phones home with no per-call-site work. WebGL2 canvas-creation and texture-allocation
// failures are just three track() call sites (see gfx/gl/glrenderer.ts + web/main.ts).
//
// A common envelope rides every event — a per-load session id, the route, the client version, and the
// live SP identity (did/account, when registered) — plus a ONE-TIME device snapshot (UA, memory, GPU
// renderer, MAX_TEXTURE_SIZE) on the first event of the session, so call sites pass only the
// event-specific fields. Best-effort by construction: prod-host gated (mirrors spTelemetryOn), errors
// swallowed (telemetry must never surface to the player), and bounded per-kind + globally so a
// per-frame failure can't spam the collector.

const ENDPOINT = 'https://api.warfareincorporated.com/api/telemetry';

export type TelemetryLevel = 'info' | 'warn' | 'error';
type IdentityFn = () => Record<string, string>;

let version = '';
// Live-read identity (did/account), registered once the shell exists — mirrors main.ts spIdentity so a
// mid-session login is reflected. Until registered (e.g. a webgl2_init_failed fired at module load,
// before boot), it's empty — version still rides, and did/account are best-effort anyway.
let identityFn: IdentityFn = () => ({});
let sessionId = '';
let installed = false;

// The one-time device snapshot: computed lazily and attached to the first event only (subsequent
// events stay tiny). envSent gates it.
let envSnapshot: Record<string, unknown> | null = null;
let envSent = false;

// Bounds so a hot failure can't flood the collector: a global cap per page-load, a per-kind cap, and
// exact-error dedup (one report per distinct kind+message per load).
let sentTotal = 0;
const perKind = new Map<string, number>();
const seenErrors = new Set<string>();
const GLOBAL_CAP = 64;
const PER_KIND_CAP = 8;
const MSG_MAX = 400;
const STACK_MAX = 1500;

// Prod-host gate — same shape as main.ts spTelemetryOn (kept in sync deliberately). Also the natural
// test/local no-op: no window (node) → disabled.
const enabled = (): boolean =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  /(^|\.)warfareincorporated\.com$/.test(window.location.hostname);

function mkSessionId(): string {
  // A per-page-load id (not crypto-strength — it only groups a session's events server-side).
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/** Install once at boot: stamp the version, mint a session id, and hook the global error handlers.
 *  Safe to call before the shell exists (identity is registered later via setTelemetryIdentity). */
export function installTelemetry(clientVersion: string): void {
  if (installed) return;
  installed = true;
  version = String(clientVersion ?? '');
  sessionId = mkSessionId();
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (e: ErrorEvent) => {
    // Resource-load failures (img/script) surface here as a bare Event with no message — skip those;
    // we only want real script exceptions.
    if (!e || (!e.message && !e.error)) return;
    // Third-party scripts (the CF Insights beacon, browser extensions) throw into window 'error'
    // too — e.g. beacon.min.js calling Array.prototype.at on pre-2021 browsers. Not ours to fix,
    // so only same-origin (or unattributed inline) errors report. A muted cross-origin error
    // surfaces as a bare "Script error." with no filename — skip that shape as well.
    const src = e.filename || '';
    if (src && !src.startsWith(location.origin)) return;
    if (!src && /^Script error\.?$/.test(e.message || '')) return;
    trackError(e.error ?? e.message, 'window_error', {
      src: e.filename || undefined,
      line: e.lineno || undefined,
      col: e.colno || undefined,
    });
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    trackError(e?.reason, 'unhandled_rejection');
  });
}

/** Register the live identity getter (did/account) — called from boot() once the shell/prefs exist. */
export function setTelemetryIdentity(fn: IdentityFn): void {
  identityFn = fn;
}

/** Emit one telemetry event. kind is a short slug (a-z0-9_.:-, <=48); data is freeform detail. */
export function track(
  kind: string,
  data?: Record<string, unknown>,
  level: TelemetryLevel = 'info',
): void {
  if (!enabled()) return;
  if (sentTotal >= GLOBAL_CAP) return;
  const k = String(kind).slice(0, 48);
  const n = perKind.get(k) ?? 0;
  if (n >= PER_KIND_CAP) return;
  perKind.set(k, n + 1);
  sentTotal++;
  try {
    const payload: Record<string, unknown> = {
      k,
      lv: level,
      sid: sessionId,
      u: location.pathname,
      v: version,
      ...identityFn(),
    };
    if (data !== undefined) payload.d = data;
    if (!envSent) {
      payload.env = snapshotEnv();
      envSent = true;
    }
    send(payload);
  } catch {
    /* telemetry must never surface to the player */
  }
}

/** Report an error/exception. Extracts message + stack, dedups exact repeats, sends at level=error. */
export function trackError(err: unknown, kind = 'error', data?: Record<string, unknown>): void {
  if (!enabled()) return;
  let msg = '';
  let stack: string | undefined;
  if (err instanceof Error) {
    msg = err.message;
    stack = err.stack;
  } else if (typeof err === 'string') msg = err;
  else {
    try {
      msg = String(err);
    } catch {
      msg = 'unknown';
    }
  }
  msg = msg.slice(0, MSG_MAX);
  const dedup = `${kind}:${msg}`;
  if (seenErrors.has(dedup)) return; // one report per distinct error per load
  seenErrors.add(dedup);
  track(kind, { msg, ...(stack ? { stack: stack.slice(0, STACK_MAX) } : {}), ...data }, 'error');
}

/** A compact device descriptor (GPU / memory / cores / DPR) pulled from the cached one-time env
 *  snapshot. The full `env` rides only a session's FIRST event, so events that need device correlation
 *  on their OWN row — the per-game `perf` sample the ops dashboard's Performance panel groups by device —
 *  attach this instead. Reuses the cached GPU probe (no second WebGL context). */
export function deviceTag(): Record<string, unknown> {
  const e = snapshotEnv();
  const t: Record<string, unknown> = {};
  for (const k of ['gpu', 'dm', 'hc', 'dpr'] as const) if (e[k] !== undefined) t[k] = e[k];
  return t;
}

// sendBeacon first — it's fire-and-forget and survives the unload that a fatal init failure often
// triggers (reload/redirect). Falls back to a keepalive fetch when sendBeacon is unavailable or
// refuses (queue full). Both swallow all errors.
function send(payload: Record<string, unknown>): void {
  const body = JSON.stringify(payload);
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // text/plain (NOT application/json): sendBeacon always sends credentials, and an
      // application/json body is not CORS-simple, so the cross-origin beacon triggered a
      // preflight that failed ("Access-Control-Allow-Origin must not be '*' when credentials
      // mode is 'include'") — the collector's CORS is a wildcard. A text/plain body is a
      // CORS-safelisted content type, so there's no preflight and the beacon just sends; the
      // collector parses the JSON body regardless of the declared content type.
      if (navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))) return;
    }
  } catch {
    /* fall through to fetch */
  }
  try {
    void fetch(ENDPOINT, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body,
    }).catch(() => {
      /* swallow */
    });
  } catch {
    /* fetch can throw synchronously — swallow */
  }
}

// One-time device snapshot for in-the-wild triage: which browsers/GPUs are failing. The GPU probe uses
// its OWN throwaway context so it still reports the renderer even when the game's WebGL2 init failed
// (a bare webgl fallback usually still exposes UNMASKED_RENDERER_WEBGL) — and 'none' when even the probe
// can't get a context, which is itself the signal.
function snapshotEnv(): Record<string, unknown> {
  if (envSnapshot !== null) return envSnapshot;
  const env: Record<string, unknown> = {};
  if (typeof navigator !== 'undefined') {
    env.ua = String(navigator.userAgent ?? '').slice(0, 300);
    const dm = (navigator as { deviceMemory?: number }).deviceMemory;
    if (typeof dm === 'number') env.dm = dm;
    if (typeof navigator.hardwareConcurrency === 'number') env.hc = navigator.hardwareConcurrency;
  }
  if (typeof window !== 'undefined') {
    env.dpr = window.devicePixelRatio;
    if (window.screen) {
      env.sw = window.screen.width;
      env.sh = window.screen.height;
    }
  }
  const gpu = probeGpu();
  if (gpu) Object.assign(env, gpu);
  envSnapshot = env;
  return env;
}

function probeGpu(): Record<string, unknown> | null {
  if (typeof document === 'undefined') return null;
  try {
    const c = document.createElement('canvas');
    const gl = (c.getContext('webgl2') ?? c.getContext('webgl')) as WebGLRenderingContext | null;
    if (gl === null) return { gpu: 'none' }; // even a probe context failed — a strong signal
    const out: Record<string, unknown> = {};
    const dbg = gl.getExtension('WEBGL_debug_renderer_info') as {
      UNMASKED_RENDERER_WEBGL: number;
      UNMASKED_VENDOR_WEBGL: number;
    } | null;
    if (dbg) {
      out.gpu = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) ?? '').slice(0, 120);
      out.gvendor = String(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) ?? '').slice(0, 80);
    }
    out.maxtex = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    // Release the probe context promptly rather than waiting on GC.
    const lose = gl.getExtension('WEBGL_lose_context') as { loseContext(): void } | null;
    if (lose) lose.loseContext();
    return out;
  } catch {
    return null;
  }
}
